import React, { FC, Fragment } from "react";

import { IconPosition } from "@at/design-system-elements";
import { IconPhone } from "@at/design-system-icons";
import { Banner } from "@at/design-system-patterns";
import { dataAttrGUI, displayNumberToDialNumber } from "@at/formatter-utils";
import { Channel } from "@at/sauron-platform-core/lib/types";

import { LeasingVehicleTypeEnum } from "../../../../__generated__/fed-graphql-types";
import { useLeasingStateContext } from "../../../../contexts/leasing/context/leasing.context";
import {
  LEASING_OPENING_HOURS,
  LEASING_OPENING_HOURS_XMAS,
  LEASING_PHONE_NUMBER,
} from "../../../../pages/leasing/leasing-product-page.constants";
import { useChannel } from "../../../../utils/hooks/use-channel";
import { getLeasingPhoneLineExceptionState } from "../../../../utils/leasing/get-leasing-phone-line-exception-state/get-leasing-phone-line-exception-state";
import { useTracking } from "../../../../utils/tracking/use-tracking";
import { OpeningHours } from "../../../opening-hours/opening-hours";
import {
  buildEnquiryQueryString,
  buildUrlWithParams,
} from "../../navigation/leasing-navigation.utils";
import * as Styled from "../leasing-modal-hub.styled";

const isInXmasPeriod = (): boolean => {
  const currentDate = new Date();
  const startDate = new Date("2024-12-22Z");
  const endDate = new Date("2025-01-04Z");

  return currentDate >= startDate && currentDate < endDate;
};

export const LeasingContactUsModal: FC = () => {
  const { trackEvent } = useTracking();
  const channel = useChannel();
  const { selectedLeaseOffer, leaseContractType, productData } =
    useLeasingStateContext();

  const vehicleType =
    channel === Channel.cars
      ? LeasingVehicleTypeEnum.CAR
      : LeasingVehicleTypeEnum.LCV;

  const datesData = isInXmasPeriod()
    ? LEASING_OPENING_HOURS_XMAS
    : LEASING_OPENING_HOURS;

  const newSearchString = buildEnquiryQueryString(
    leaseContractType,
    vehicleType,
    selectedLeaseOffer,
    productData?.id,
  );

  const urlWithParams = buildUrlWithParams(
    newSearchString,
    `/${channel}/leasing/request-a-callback`,
  );

  const showPhoneLinesClosedBanner = getLeasingPhoneLineExceptionState();

  return (
    <Fragment>
      <Styled.LeasingProductModalHeaderAndParagraphContainer>
        <Styled.LeasingProductModalHeader>
          Contact Us
        </Styled.LeasingProductModalHeader>
        {showPhoneLinesClosedBanner && (
          <Banner
            variation="warning"
            {...{ [dataAttrGUI]: "phone-lines-closed-banner" }}
          >
            Our phone lines will be closed on 16 April. If you would like to
            contact our team during this time, please email{" "}
            <Styled.EmailLink href="mailto:leasing-enquiries@autotrader.co.uk">
              leasing-enquiries@autotrader.co.uk
            </Styled.EmailLink>
          </Banner>
        )}
        <Styled.LeasingModalCTA
          {...{ [dataAttrGUI]: "call-leasing-button" }}
          url={`tel:${displayNumberToDialNumber(LEASING_PHONE_NUMBER)}`}
          iconPosition={IconPosition.LEFT}
          iconSrc={<IconPhone title="" />}
          onInteraction={() => trackEvent({ trackingLabel: "call now" })}
          text={LEASING_PHONE_NUMBER}
          displayType="secondary"
          fullWidth
        />
        <Styled.LeasingModalCTA
          {...{ attributes: { [dataAttrGUI]: "call-leasing-button" } }}
          url={urlWithParams}
          onInteraction={() =>
            trackEvent({ trackingLabel: "request-callback" })
          }
          text="Request callback"
          displayType="secondary"
          fullWidth
        />
      </Styled.LeasingProductModalHeaderAndParagraphContainer>
      <Styled.LeasingProductModalHeaderAndParagraphContainer>
        <Styled.LeasingProductModalInfoBlock>
          <Styled.LeasingProductModalInfoBlockTitle as="h3">
            Opening hours
          </Styled.LeasingProductModalInfoBlockTitle>
          <OpeningHours data={datesData} />
        </Styled.LeasingProductModalInfoBlock>
      </Styled.LeasingProductModalHeaderAndParagraphContainer>
    </Fragment>
  );
};
